import { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import SecureChat from '../components/Chat/SecureChat';
import {
  ChatBubbleLeftRightIcon,
  MegaphoneIcon,
  DocumentTextIcon, 
  ShieldCheckIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { toast } from 'react-hot-toast';

interface EncryptionKey {
  id: string;
  name: string;
  uploadDate: string;
  status: 'active' | 'inactive';
  expiryDate: string;
}

export default function Communications() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [encryptionKeys, setEncryptionKeys] = useState<EncryptionKey[]>([
    {
      id: '1',
      name: 'primary-key-2024.pem',
      uploadDate: '2024-01-15',
      status: 'active',
      expiryDate: '2024-12-31'
    },
    {
      id: '2',
      name: 'backup-key-2024.pem',
      uploadDate: '2024-01-15',
      status: 'inactive',
      expiryDate: '2024-12-31'
    }
  ]);
  const [selectedKey, setSelectedKey] = useState<string>(
    encryptionKeys.find(key => key.status === 'active')?.id || ''
  );
  
  useEffect(() => {
    // Enable/disable save button based on file and template type selection
    const fileInput = document.getElementById('fileUpload') as HTMLInputElement;
    const templateType = document.getElementById('templateType') as HTMLSelectElement;
    const saveButton = document.getElementById('saveButton') as HTMLButtonElement;

    const updateSaveButton = () => {
      const fileSelected = fileInput?.files?.length > 0;
      const typeSelected = templateType?.value !== '';
      if (saveButton) {
        saveButton.disabled = !(fileSelected && typeSelected);
      }
    };

    fileInput?.addEventListener('change', updateSaveButton);
    templateType?.addEventListener('change', updateSaveButton);

    // Add click handler for save button
    saveButton?.addEventListener('click', () => {
      const fileName = fileInput?.files?.[0]?.name;
      const type = templateType?.value;
      const timestamp = new Date().toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      // Update status panel
      document.getElementById('uploadTimestamp')!.textContent = timestamp;
      document.getElementById('uploadedTemplate')!.textContent = fileName || '-';
      document.getElementById('uploadedType')!.textContent = type || '-';

      const statusMessage = document.getElementById('statusMessage')!;
      statusMessage.className = 'mt-2 text-sm text-green-600';
      statusMessage.textContent = 'Template uploaded successfully';

      // Reset form
      fileInput.value = '';
      templateType.value = '';
      document.getElementById('selectedFileName')?.setAttribute('value', '');
      saveButton.disabled = true;
    });

    return () => {
      fileInput?.removeEventListener('change', updateSaveButton);
      templateType?.removeEventListener('change', updateSaveButton);
    };
  }, []);
  const [categories] = useState([
    {
      id: 'internal',
      name: 'Internal Communications',
      icon: ChatBubbleLeftRightIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-4">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Internal Communication Channels</h3>
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Secure Messaging Platform</h4>
                  <p className="text-sm text-gray-600 mt-1">Real-time secure communication for team members</p>
                  <button 
                    onClick={() => setIsChatOpen(true)}
                    className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Open Chat
                  </button>
                </div>
                <div className="mt-4">
                  <h4 className="font-medium">Pre-approved Templates</h4>
                  <div className="mt-4 grid grid-cols-1 gap-4">
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium">Initial Incident Notification</h4>
                      <p className="text-sm text-gray-600 mt-1">Template for first communication about an incident</p>
                      <div className="mt-2 flex space-x-2">
                        <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                          Use Template
                        </button>
                        <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                          Preview
                        </button>
                      </div>
                    </div>
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium">Status Update</h4>
                      <p className="text-sm text-gray-600 mt-1">Template for ongoing incident updates</p>
                      <div className="mt-2 flex space-x-2">
                        <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                          Use Template
                        </button>
                        <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                          Preview
                        </button>
                      </div>
                    </div>
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium">Resolution Notice</h4>
                      <p className="text-sm text-gray-600 mt-1">Template for incident closure communication</p>
                      <div className="mt-2 flex space-x-2">
                        <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                          Use Template
                        </button>
                        <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                          Preview
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'external',
      name: 'External Communications',
      icon: MegaphoneIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-4">
            {/* Disclaimer Notice */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 font-medium">DISCLAIMER:</p>
              <p className="text-yellow-700 text-sm mt-1">
                These are draft templates that require approval from your official Company representatives. 
                Verify and seek approval from your official Company representatives before using. 
                The software platform provider does not guarantee accuracy of these templates.
              </p>
            </div>

            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">External Communication Channels</h3>
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Media Statements</h4>
                  <p className="text-sm text-gray-600 mt-1">Prepare and distribute press releases</p>
                  <button className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Download Media Template
                  </button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Customer Notifications</h4>
                  <p className="text-sm text-gray-600 mt-1">Send updates to affected customers</p>
                  <button className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Download Customer Template
                  </button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Regulatory Reports</h4>
                  <p className="text-sm text-gray-600 mt-1">Submit required regulatory filings</p>
                  <button className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Download Regulatory Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'templates',
      name: 'Communication Templates',
      icon: DocumentTextIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-4">
            {/* Template Upload Interface */}
            <div className="border-b pb-6 mb-6">
              <h3 className="text-lg font-medium mb-4">Upload Template</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="templateType" className="block text-sm font-medium text-gray-700">
                    Template Type
                  </label>
                  <select
                    id="templateType"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="">Select template type...</option>
                    <option value="media">Media Template</option>
                    <option value="customer">Customer Template</option>
                    <option value="regulatory">Regulatory Template</option>
                    <option value="initial">Initial Incident Notification Template</option>
                    <option value="status">Status Update Template</option>
                    <option value="resolution">Resolution Notice Template</option>
                  </select>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Selected File
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                      <input
                        type="text"
                        className="flex-1 rounded-l-md border-gray-300 bg-gray-50 sm:text-sm"
                        placeholder="No file selected"
                        readOnly
                        id="selectedFileName"
                      />
                      <label
                        htmlFor="fileUpload"
                        className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                      >
                        Upload
                      </label>
                      <input
                        type="file"
                        id="fileUpload"
                        className="hidden"
                        accept=".doc,.docx,.pdf"
                        onChange={(e) => {
                          const fileName = e.target.files?.[0]?.name;
                          if (fileName) {
                            document.getElementById('selectedFileName')?.setAttribute('value', fileName);
                          }
                        }}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled
                    id="saveButton"
                  >
                    Save
                  </button>
                </div>

                {/* Upload Status Panel */}
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Upload Status</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Timestamp:</span>
                      <span className="text-gray-900" id="uploadTimestamp">-</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Template Name:</span>
                      <span className="text-gray-900" id="uploadedTemplate">-</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Template Type:</span>
                      <span className="text-gray-900" id="uploadedType">-</span>
                    </div>
                    <div id="statusMessage"></div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      ),
    },
    {
      id: 'admin',
      name: 'Admin',
      icon: ShieldCheckIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            {/* Admin Only Label */}
            <div className="text-center bg-red-50 border border-red-200 rounded-lg p-2">
              <span className="text-red-600 font-medium">Admin Only</span>
            </div>

            {/* Encryption Key Management */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-medium mb-4">Encryption Key Management</h3>
              
              {/* Upload Section */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="flex items-center space-x-4">
                  <KeyIcon className="h-6 w-6 text-gray-400" />
                  <div className="flex-1">
                    <label
                      htmlFor="key-upload"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 cursor-pointer"
                    >
                      Upload Encryption Key
                      <input
                        id="key-upload"
                        type="file"
                        className="hidden"
                        accept=".key,.pem"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const newKey: EncryptionKey = {
                              id: Date.now().toString(),
                              name: file.name,
                              uploadDate: new Date().toISOString().split('T')[0],
                              status: 'inactive',
                              expiryDate: '2024-12-31'
                            };
                            setEncryptionKeys([...encryptionKeys, newKey]);
                            toast.success('Encryption key uploaded successfully');
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Keys List */}
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Key Name</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Upload Date</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Expiry Date</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Set Active</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {encryptionKeys.map((key) => (
                      <tr key={key.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                          {key.name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {key.uploadDate}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {key.expiryDate}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {key.status === 'active' ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircleIcon className="mr-1 h-4 w-4" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <XCircleIcon className="mr-1 h-4 w-4" />
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <input
                            type="radio"
                            name="active-key"
                            checked={selectedKey === key.id}
                            onChange={() => setSelectedKey(key.id)}
                            className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Save Changes Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setEncryptionKeys(keys => 
                      keys.map(key => ({
                        ...key,
                        status: key.id === selectedKey ? 'active' : 'inactive'
                      }))
                    );
                    toast.success('Changes saved successfully');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Communications</h2>
      <Tab.Group>
        <Tab.List className="flex space-x-4 border-b border-gray-200">
          {categories.map((category) => (
            <Tab
              key={category.id}
              className={({ selected }) =>
                clsx(
                  'flex items-center space-x-2 py-2 px-4 text-sm font-medium border-b-2 outline-none',
                  selected
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )
              }
            >
              <category.icon className="h-5 w-5" />
              <span>{category.name}</span>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {categories.map((category) => (
            <Tab.Panel key={category.id}>{category.content}</Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
      <SecureChat isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </div>
  );
}